import React from 'react';
import { motion } from 'framer-motion';
import { Star, Zap, DollarSign, Palette } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function Features() {
  const {
    t
  } = useLanguage();
  const features = [{
    icon: Star,
    title: t.features.quality.title,
    desc: t.features.quality.desc,
    gradient: 'from-yellow-400 to-orange-500',
    shadow: 'shadow-yellow-500/20',
    iconColor: 'text-yellow-100'
  }, {
    icon: Zap,
    title: t.features.fast.title,
    desc: t.features.fast.desc,
    gradient: 'from-blue-400 to-cyan-500',
    shadow: 'shadow-blue-500/20',
    iconColor: 'text-blue-100'
  }, {
    icon: DollarSign,
    title: t.features.cheap.title,
    desc: t.features.cheap.desc,
    gradient: 'from-green-400 to-emerald-500',
    shadow: 'shadow-green-500/20',
    iconColor: 'text-green-100'
  }, {
    icon: Palette,
    title: t.features.beautiful.title,
    desc: t.features.beautiful.desc,
    gradient: 'from-purple-400 to-pink-500',
    shadow: 'shadow-purple-500/20',
    iconColor: 'text-purple-100'
  }];
  return <section className="py-32 bg-warm-50 dark:bg-[#050505] transition-colors duration-500 relative overflow-hidden" data-id="element-89">
      {/* Ambient Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none" data-id="element-90">
        <div className="absolute top-[20%] left-[10%] w-[500px] h-[500px] bg-accent-blue/5 rounded-full blur-[120px]" data-id="element-91" />
        <div className="absolute bottom-[20%] right-[10%] w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[120px]" data-id="element-92" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-93">
        <div className="text-center mb-24" data-id="element-94">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight" data-id="element-95">
            {t.features.title}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto" data-id="element-96">
            {t.features.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-97">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -10
        }} className="group relative bg-white dark:bg-[#0A0A0A] p-8 rounded-3xl border border-gray-100 dark:border-white/5 hover:border-gray-200 dark:hover:border-white/10 transition-all duration-500" data-id="element-98">
              {/* Hover Glow Effect */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} data-id="element-99" />

              {/* Premium Icon Container */}
              <div className="relative mb-8 inline-block" data-id="element-100">
                {/* Outer Glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500`} data-id="element-101" />

                {/* Icon Background */}
                <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-lg ${feature.shadow} group-hover:scale-110 transition-transform duration-500`} data-id="element-102">
                  {/* Inner Shine */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/20 to-transparent opacity-50" data-id="element-103" />

                  <feature.icon className={`w-8 h-8 ${feature.iconColor} relative z-10 drop-shadow-md`} data-id="element-104" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 dark:group-hover:from-white dark:group-hover:to-gray-300 transition-all duration-300" data-id="element-105">
                {feature.title}
              </h3>

              <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-base group-hover:text-gray-900 dark:group-hover:text-gray-300 transition-colors duration-300" data-id="element-106">
                {feature.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}